
import { Component, Provide, Vue, Watch } from 'vue-property-decorator';
import { MediaApiControllerUIClient, MediaItem, Overview2DSettings, SettingsApiControllerUIClient } from '../../backend/backendapi';
import { getMarkerWithObjects, getFilteredMarkers, readMarkers, writeMarkers } from './util';
import { handleError, initClient } from '../../../api';
import { fixISettings } from '../../../util';
import State from './state';
import ImageSelector from './ImageSelector';
import MarkerCanvas from './MarkerCanvas';
import MarkerList from './MarkerList';
import ZoomControls from './ZoomControls';
import { Marker } from './types';

@Component({
  components: {
    ImageSelector,
    MarkerCanvas,
    MarkerList,
    ZoomControls,
  },
})
export default class Overview2d extends Vue {
  private readonly mediaApi = initClient(MediaApiControllerUIClient);
  private readonly settingApi = initClient(SettingsApiControllerUIClient);

  overview2DSettings = new Overview2DSettings();

  @Provide()
  readonly state = new State();

  private lastSavedMarkers: string = '[]';

  get healthyMarkersCount(): number {
    return getMarkerWithObjects(this.state).filter(e => e.activities.length === 0).length;
  }

  get issueMarkersCount(): number {
    return getMarkerWithObjects(this.state).filter(e => e.activities.length > 0).length;
  }

  get issuePercent(): number {
    const total = this.state.markers.length;
    console.log('issuePercent ' + this.issueMarkersCount + ' total: ' + total);
    return total === 0 ? 0 : (this.issueMarkersCount / total) * 100;
  }

  get rowsCount(): number {
    return getFilteredMarkers(this.state).length;
  }

  get modified(): boolean {
    return JSON.stringify(this.state.markers) !== this.lastSavedMarkers;
  }

  get fileName(): string {
    return this.state.media?.filename ?? '-';
  }

  async mounted(): Promise<void> {
    this.state.canvasRef = this.$refs.canvas as MarkerCanvas;
    await this.reloadUserSetting();
    if (this.overview2DSettings.lastSelectedMediaId) {
      const media = await this.mediaApi.get(this.overview2DSettings.lastSelectedMediaId);
      await this.onMediaSelected(media);
    }
  }

  async discard(): Promise<void> {
    if (!this.state.media) {
      return;
    }
    this.state.media = await this.mediaApi.get(this.state.media.id!);
  }

  async reloadUserSetting(): Promise<void> {
    fixISettings();
    const set2 = (await this.settingApi.get('Overview2DSettings', false, true)).result!;
    this.overview2DSettings = new Overview2DSettings();
    this.overview2DSettings.init(set2);
  }

  async save(): Promise<void> {
    if (!this.state.media) {
      return;
    }
    try {
      await this.mediaApi.saveMeta(this.state.media);
      this.lastSavedMarkers = JSON.stringify(readMarkers(this.state.media));
      this.$toasted.info(this.$t('txtSaved').toString());
    } catch (e) {
      handleError(this.$toasted, this.$i18n, e);
    }
  }

  async onMediaSelected(e: MediaItem): Promise<void> {
    e.properties ??= [];
    this.state.media = e;
    this.state.selectedMarkerId = null;
    this.lastSavedMarkers = JSON.stringify(readMarkers(e));

    this.$nextTick(() => this.state.canvasRef.fitToContainer());
    await this.saveSetting(e);
  }

  async saveSetting(e: MediaItem): Promise<void> {
    if (e.id !== this.overview2DSettings.lastSelectedMediaId) {
      this.overview2DSettings.lastSelectedMediaId = e.id;
      await this.settingApi.update('Overview2DSettings', false, true, JSON.stringify(this.overview2DSettings));
    }
  }

  @Watch('state.markers', { deep: true })
  onMarkersChanged() {
    if (!this.state.media) {
      return;
    }
    writeMarkers(this.state.media, this.state.markers);
  }

  @Watch('state.media.properties', { deep: true })
  onMediaPropertiesChanged() {
    if (!this.state.media) {
      return;
    }

    const markers = readMarkers(this.state.media);
    if (JSON.stringify(markers) !== JSON.stringify(this.state.markers)) {//only update when different
      this.state.markers.splice(0);
      this.state.markers.push(...markers);
    }
  }


  /**
   * Performance Testing Utility: Spawns 500 active issue markers across the current media map layout.
   * If needed insert into mounted() for auto-generation on load
   */
  generateTestMarkers(): void {
    if (!this.state.media) {
      this.$toasted.error('Please load an image first before generating test data.');
      return;
    }

    this.state.markers.splice(0);

    const testMarkers: Marker[] = [];
    const Cnt = 100;
    for (let i = 1; i <= Cnt; i++) {
      // Distribute randomly across normalized space coordinates (0.0 to 1.0)
      const randomX = Math.random();
      const randomY = Math.random();

      testMarkers.push({
        id: `test-marker-${i}-${crypto.randomUUID().substring(0, 8)}`,
        nx: randomX,
        ny: randomY,
        label: `Test Component Node #${i}`,
        category: `Performance Benchmark`,
        color: 'red',
        key: `test-key-${i}`,
      });
    }


    this.state.markers.push(...testMarkers);

    writeMarkers(this.state.media, this.state.markers);

    this.$nextTick(() => {
      this.state.canvasRef?.fitToContainer();
    });

    this.$toasted.success(`Successfully generated ${Cnt} pulsating issue test markers!`);
  }
}
